// [PURPOSE]: Pure helper that assembles the diagnostic debug-bundle JSON object.
//   Extracted from App.jsx so it can be unit-tested without a DOM/React context.
//   Caller (App.jsx exportDebugBundle) provides dynamic values (characterData,
//   idbHealth, timestamp) so the helper stays deterministic in tests.
//
// [INTEGRATION]: App.jsx calls buildDebugBundle() then triggers a download via
//   the same createObjectURL pattern used by exportCharacter().
//
// [RULE 22]: Production logic lives here; both App.jsx and the test import it.
//   No inline replica in the test.

import { APP_VERSION } from '../constants/version';

/**
 * Compute localStorage used bytes by summing key + value lengths (UTF-16
 * character count × 2 is a rough byte approximation; actual stored bytes vary
 * by browser but this is the canonical supportable metric).
 *
 * @returns {{ usedChars: number, keyCount: number }}
 */
export function localStorageStats() {
  let usedChars = 0;
  let keyCount = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      keyCount++;
      usedChars += key.length;
      const val = localStorage.getItem(key);
      if (val) usedChars += val.length;
    }
  } catch (_) {
    // Private browsing or storage access error — return what we have so far.
  }
  return { usedChars, keyCount };
}

/**
 * Collect safe browser environment info. All fields are guarded so the bundle
 * never throws in restricted contexts (iOS WKWebView, Capacitor, private mode).
 *
 * @returns {Object}
 */
export function browserInfo() {
  const info = {
    userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unavailable',
    viewport: {
      width: (typeof window !== 'undefined' && window.innerWidth) || null,
      height: (typeof window !== 'undefined' && window.innerHeight) || null,
    },
    language: (typeof navigator !== 'undefined' && navigator.language) || null,
    online: (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean')
      ? navigator.onLine
      : null,
  };

  // window.performance.memory is Chrome-only and not spec'd; guard it.
  try {
    if (
      typeof window !== 'undefined' &&
      window.performance &&
      window.performance.memory
    ) {
      const m = window.performance.memory;
      info.performanceMemory = {
        usedJSHeapSize: m.usedJSHeapSize,
        totalJSHeapSize: m.totalJSHeapSize,
        jsHeapSizeLimit: m.jsHeapSizeLimit,
      };
    }
  } catch (_) { /* not available */ }

  return info;
}

/**
 * Assemble the diagnostic bundle. All dynamic / async-derived values are
 * passed in as parameters so this function is synchronous and testable.
 *
 * @param {Object}  opts
 * @param {Object}  opts.characterData   - Output of buildCurrentCharacterData()
 * @param {Object}  opts.idbHealth       - { available, keyCount, error? }
 * @param {string}  opts.timestamp       - ISO timestamp string (caller provides
 *                                         new Date().toISOString() in production;
 *                                         test passes a fixed value)
 * @returns {Object} The full debug bundle, ready to JSON.stringify
 */
export function buildDebugBundle({ characterData, idbHealth, timestamp }) {
  const lsStats = localStorageStats();

  return {
    // ── Identity
    bundleType: 'tti-debug-report',
    appVersion: APP_VERSION,
    timestamp,

    // ── Character snapshot
    characterData: characterData || null,

    // ── Storage health
    storage: {
      indexedDb: idbHealth,
      localStorage: {
        usedChars: lsStats.usedChars,
        // Approximate bytes: JS strings are UTF-16 so ×2 per char is an upper bound
        approxBytes: lsStats.usedChars * 2,
        keyCount: lsStats.keyCount,
      },
    },

    // ── Browser env
    browser: browserInfo(),
  };
}

/**
 * Trigger a file download of the given content string.
 * Mirrors the inline download helper used elsewhere in App.jsx.
 *
 * @param {string} content  - String to write
 * @param {string} filename - Suggested filename
 * @param {string} mimeType - MIME type (default application/json)
 */
export function triggerBundleDownload(content, filename, mimeType = 'application/json') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
